import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { PricingBasisChip } from "./PricingBasisChip";
import { PackDiscrepancyChip } from "./PackDiscrepancyChip";
import { DualPriceDisplay } from "./DualPriceDisplay";
import { QuantityVarianceDisplay } from "./QuantityVarianceDisplay";
import { Edit3, Save, X } from "lucide-react";
import { Product as WorkstationProduct } from "@/hooks/useWorkstationStore";

interface InvoiceItem {
  id: string;
  product_name: string;
  barcode_upc?: string;
  size_string?: string;
  pack_qty?: number;
  units_per_case?: number;
  price_case?: number;
  price_unit?: number;
  extended?: number;
  pricing_basis?: string;
  raw_terms?: any;
  normalized?: any;
  matched_product_id?: string;
  validation_status?: string;
}

interface EnhancedInvoiceItemRowProps {
  invoiceItem: InvoiceItem;
  matchedProduct?: WorkstationProduct;
  onUpdatePack?: (itemId: string, newPack: number) => void;
  onResolveQuantityVariance?: (itemId: string, action: string) => void;
  className?: string;
}

export function EnhancedInvoiceItemRow({
  invoiceItem,
  matchedProduct,
  onUpdatePack,
  onResolveQuantityVariance,
  className
}: EnhancedInvoiceItemRowProps) {
  const [isEditingPack, setIsEditingPack] = useState(false);
  const [editPackValue, setEditPackValue] = useState(invoiceItem.units_per_case?.toString() || '');

  const handleSavePack = () => {
    const newPack = parseInt(editPackValue);
    if (!isNaN(newPack) && newPack > 0) {
      onUpdatePack?.(invoiceItem.id, newPack);
      setIsEditingPack(false);
    }
  };

  const handleCancelPack = () => {
    setEditPackValue(invoiceItem.units_per_case?.toString() || '');
    setIsEditingPack(false);
  };

  // Calculate received pricing for comparison
  const receivedUnitPrice = matchedProduct?.costPerUnit || null;
  const receivedCasePrice = matchedProduct?.costPerBox || 
    (receivedUnitPrice && matchedProduct?.unitsPerBox ? 
      receivedUnitPrice * matchedProduct.unitsPerBox : null);

  // Calculate received quantities
  const receivedUnits = matchedProduct ? matchedProduct.boxes * matchedProduct.unitsPerBox : null;

  return (
    <Card className={className}>
      <CardContent className="p-4">
        <div className="space-y-4">
          {/* Header with product name and status chips */}
          <div className="flex items-start justify-between">
            <div className="space-y-2">
              <h4 className="font-medium">{invoiceItem.product_name}</h4>
              {invoiceItem.size_string && (
                <Badge variant="outline" className="text-xs">
                  {invoiceItem.size_string}
                </Badge>
              )}
            </div>
            
            <div className="flex items-center space-x-2">
              <PricingBasisChip 
                basis={invoiceItem.pricing_basis as any || 'unknown'}
                rawText={invoiceItem.raw_terms?.price_basis_text}
              />
              
              {isEditingPack ? (
                <div className="flex items-center space-x-1">
                  <Input
                    value={editPackValue}
                    onChange={(e) => setEditPackValue(e.target.value)}
                    className="w-20 h-8"
                    placeholder="Pack"
                  />
                  <Button size="sm" variant="outline" onClick={handleSavePack}>
                    <Save className="w-3 h-3" />
                  </Button>
                  <Button size="sm" variant="outline" onClick={handleCancelPack}>
                    <X className="w-3 h-3" />
                  </Button>
                </div>
              ) : (
                <PackDiscrepancyChip
                  invoicePack={invoiceItem.units_per_case}
                  receivedPack={matchedProduct?.unitsPerBox}
                  onEditPack={() => setIsEditingPack(true)}
                />
              )}
            </div>
          </div>

          <Separator />

          {/* Dual pricing comparison */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <DualPriceDisplay
              label="Invoice Pricing"
              unitPrice={invoiceItem.normalized?.price_unit || invoiceItem.price_unit}
              casePrice={invoiceItem.normalized?.price_case || invoiceItem.price_case}
              comparisonUnitPrice={receivedUnitPrice}
              comparisonCasePrice={receivedCasePrice}
              rawTerms={{
                price_basis_text: invoiceItem.raw_terms?.price_basis_text,
                derivedMethod: invoiceItem.normalized ? 'Computed from available data' : undefined
              }}
            />
            
            {matchedProduct && (
              <DualPriceDisplay
                label="Received Pricing"
                unitPrice={receivedUnitPrice}
                casePrice={receivedCasePrice}
                comparisonUnitPrice={invoiceItem.normalized?.price_unit || invoiceItem.price_unit}
                comparisonCasePrice={invoiceItem.normalized?.price_case || invoiceItem.price_case}
              />
            )}
          </div>

          <Separator />

          {/* Quantity comparison */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <h5 className="text-sm font-medium mb-2">Invoice Quantities</h5>
              <QuantityVarianceDisplay
                invoicePackQty={invoiceItem.pack_qty}
                invoiceUnits={invoiceItem.normalized?.qty_units}
                receivedPackQty={matchedProduct?.boxes}
                receivedUnits={receivedUnits}
                onResolve={(action) => onResolveQuantityVariance?.(invoiceItem.id, action)}
              />
            </div>
            
            {matchedProduct && (
              <div>
                <h5 className="text-sm font-medium mb-2">Received Quantities</h5>
                <div className="space-y-1">
                  <div className="flex items-center space-x-2">
                    <span className="text-sm">
                      {matchedProduct.boxes} cases
                    </span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className="text-sm">
                      {receivedUnits} units
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Extended total comparison */}
          {invoiceItem.extended && (
            <div className="pt-2 border-t">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">Line Total</span>
                <span className="text-sm">${invoiceItem.extended.toFixed(2)}</span>
              </div>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}